import React, {useEffect} from 'react';
import {Location} from '@reach/router';
import qs from 'qs';
import {DateTime} from 'luxon';
import {Col, Container, Row, Button} from 'reactstrap';
import classNames from 'classnames';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faCartPlus} from '@fortawesome/pro-light-svg-icons/faCartPlus';
import {subscribeToCampaign} from 'react-controlled-ab/datalayers/vwo';

import {usePhaseState} from 'funnel-schedule/phase-context';
import useBeacon from 'utils/useBeacon';
import useLetReach from 'utils/useLetReach';
import {useModal} from 'contexts/modal';
import {useUserState} from 'contexts/user';

import PageMeta from 'components/PageMeta';
import Header from 'components/Header';
import Video from 'components/Video';
import Footer from 'components/Footer';
import {WlcSpeakerList} from 'components/SpeakerList';
import CommentSection, {CommentSectionInline} from 'components/CommentSection';
import CheckoutButton from 'components/CheckoutButton';
import CountdownTimer from 'components/Elements/CountdownTimer';
import VideoButtons from 'components/Broadcasts/VideoButtons';
import {SalesDisclaimer} from 'components/SalesCta';
import SectionCL from 'components/ComponentLibrary/SectionCL';
import IconListCL from 'components/ComponentLibrary/IconListCL/IconListCL';
import useBalanceText from 'utils/useBalanceText';

export default function (props) {
  return <Location>{({location}) => <Broadcasts location={location} {...props} />}</Location>;
}

function Broadcasts({location, ...props}) {
  const {
    phases: {
      default: defaultSchedule = {
        phase: 'before',
        currentActionHour: 0,
        actionHours: [
          {
            date: '',
            description: '',
            speaker: [{name: '', shortName: '', bio: '', image: '', url: ''}],
            title: '',
            video: '',
            thumb: '',
            recipe: ''
          }
        ],
        noBroadcastSales: false,
        broadcastMonthOverride: ''
      },
      sales: salesSchedule = {
        phase: 'before',
        salesEndDate: '',
        salesCtaCopy: ''
      }
    }
  } = usePhaseState();

  const commentsUrl = defaultSchedule.broadcastCommentsUrl
    ? defaultSchedule.broadcastCommentsUrl
    : 'http://wlc.foodrevolution.org/broadcasts/';

  const isSpecialLaunch = defaultSchedule.specialLaunch.isActive;
  const isSeries = defaultSchedule.actionHours.length > 1;
  const currentActionHour = defaultSchedule.currentActionHour;
  const broadcastTime = new Date(defaultSchedule.actionHours[currentActionHour].date).getTime();
  const broadcastVideo = defaultSchedule.actionHours[currentActionHour].video;
  const broadcastDate = DateTime.fromISO(defaultSchedule.actionHours[currentActionHour].date);
  const notFinalActionHour = currentActionHour + 1 < defaultSchedule.actionHours.length;

  const isSales = isSeries || isSpecialLaunch;
  const productSku = 'WLC';
  const productSlug = 'wlc-30daytrial-promo-special-197';
  const productValue = 1;
  const salesEnd = new Date(salesSchedule.salesEndDate).getTime();

  const actionHourPhase = defaultSchedule.phase;

  const nextBroadcastTime = notFinalActionHour
    ? new Date(defaultSchedule.actionHours[currentActionHour + 1].date).getTime()
    : null;

  const salesTimer = new Date('2020-06-02T00:00:00-07:00').getTime();

  const {toggle: toggleModal} = useModal();
  const {email, registrationId} = useUserState();
  useEffect(
    () => !email && !registrationId && !qs.parse(location.search, {ignoreQueryPrefix: true}).o && toggleModal(),
    []
  );

  useBeacon();
  useLetReach();
  useBalanceText();
  return (
    <div className="page page-nosocial">
      <PageMeta title="Broadcasts" description="Make FOOD the foundation of YOUR health" />

      <div className="page-content">
        <Header bgColor="black" isSeries={isSeries} lockupWhite />

        <SectionCL id="" color="purple" className="text-white" innerClass="py-4" angle="none">
          <Container>
            <Row className="d-flex justify-content-center text-center">
              <Col lg="10">
                {!isSales ? (
                  <h3 className="section-only-heading">Watch This Month’s Action Hour Below!</h3>
                ) : (
                  <>
                    <h3 className="section-only-heading">
                      {
                        {
                          before: <>Welcome to the Broadcast Page!</>,
                          during: <>The Action Hour is Going on Now — Watch Below!</>,
                          after: <>The Replay of the Action Hour is Available Below.</>
                        }[actionHourPhase]
                      }
                    </h3>
                    {defaultSchedule.phase === 'before' && currentActionHour === 0 ? (
                      <>
                        <h5 className="broadcast-timer-heading text-uppercase mt-4 mb-2">
                          {isSeries ? <>The Action Hour Series Starts In:</> : <>The Action Hour Starts In:</>}
                        </h5>
                        <div>
                          <CountdownTimer time={broadcastTime} />
                        </div>
                      </>
                    ) : defaultSchedule.phase === 'before' ? (
                      <>
                        <h5 className="broadcast-timer-heading text-uppercase mt-4 mb-2">
                          The Next Action Hour Starts In:
                        </h5>
                        <div>
                          <CountdownTimer time={broadcastTime} />
                        </div>
                      </>
                    ) : defaultSchedule.phase === 'after' && notFinalActionHour ? (
                      <>
                        <h5 className="broadcast-timer-heading text-uppercase mt-4 mb-2">
                          The Next Action Hour Starts In:
                        </h5>
                        <div>
                          <CountdownTimer time={nextBroadcastTime} />
                        </div>
                      </>
                    ) : null}
                  </>
                )}
              </Col>
            </Row>
          </Container>
        </SectionCL>

        <SectionCL id="broadcast-video" color="white" order="first" innerClass="pt-0 pt-sm-4 pt-lg-5">
          <Container fluid>
            <Row className="px-xl-5">
              <Col xs="12" lg="8" className="px-0 px-sm-3">
                <Video url={broadcastVideo ? broadcastVideo : 'https://www.youtube.com/watch?v=TQ9gNpP4wQM'} playing />
                <div className="mt-2">
                  <VideoButtons />
                </div>
              </Col>
              <Col xs="12" lg="4" className="d-none d-lg-flex">
                <div className="box-shadow-black-25 rounded w-100" style={{position: 'relative'}}>
                  <CommentSectionInline id="comments" url={commentsUrl} numPosts="10" isBroadcastPage />
                </div>
              </Col>
            </Row>
          </Container>
        </SectionCL>

        <SectionCL id="broadcast-speakers" color="light-gray" className={classNames(!isSales && `speakers-evergreen`)}>
          <Container>
            <Row className="d-flex justify-content-center">
              <Col lg="10">
                <div className="background-green text-white p-3 rounded mb-3">
                  <h4 className="section-only-heading text-center">
                    {defaultSchedule.broadcastMonthOverride
                      ? defaultSchedule.broadcastMonthOverride
                      : broadcastDate.setZone('America/Los_Angeles').toFormat('MMMM')}{' '}
                    {isSeries ? 'Action Hour Series Lineup' : 'Action Hour'}
                  </h4>
                </div>
                <div className="speaker-info mb-0">
                  <WlcSpeakerList broadcastList />
                </div>
              </Col>
            </Row>
          </Container>
        </SectionCL>

        {isSales && (
          <>
            <SectionCL id="" color="black" innerClass="text-center text-white">
              <Container>
                <Row>
                  <Col>
                    <h3 className="section-heading">
                      Want to put it all into action for life? Join FRN’s WHOLE Life Club today!
                    </h3>
                    <h5 className="m-0">
                      <i>Action Hour Special: $1 for the first month — then save 60% (keep reading for the details)</i>
                    </h5>
                  </Col>
                </Row>
              </Container>
            </SectionCL>
            <SectionCL
              id=""
              color="light-gray"
              imgPosition="right" //@ts-ignore
              imgUrl={require('static/img-recipes.jpg')}
            >
              <Container>
                <Row>
                  <Col>
                    <h2 className="section-heading">What will you get?</h2>
                    <h4 className="mt-0">
                      A wind at your back to help you implement, sustain, and optimize your healthy lifestyle. Plus…
                    </h4>
                    <IconListCL>
                      <>
                        1,000+ mouth insanely delicious and abundantly healthy <b>plant-based</b> recipes your whole
                        family will love
                      </>
                      <>
                        Live Monthly Action Hours with guest experts to accelerate your learning and answer YOUR top
                        questions
                      </>
                      <>
                        A vault of 1,000+ articles to help you <b>slash your risk</b> of cancer, heart disease, type 2
                        diabetes, and more
                      </>
                      <>
                        Action of the week videos, action checklists, and live events customized to YOUR interests and
                        needs
                      </>
                    </IconListCL>
                  </Col>
                </Row>
              </Container>
            </SectionCL>
            <SectionCL
              id=""
              color="white" //@ts-ignore
              imgUrl={require('static/img-wisdom.jpg')}
              imgPosition="left"
            >
              <Container>
                <Row>
                  <Col>
                    <h4 className="section-heading">Getting your questions answered.</h4>
                    <IconListCL>
                      <>
                        <b>Submit questions</b> for future live Action Hours.
                      </>
                      <>
                        Ask YOUR questions to our brilliant team of <b>nutritionists and health coaches.</b>
                      </>
                      <>
                        Discover what you didn’t even know you needed — that will <b>revolutionize</b> your quality of
                        life.
                      </>
                    </IconListCL>
                  </Col>
                </Row>
              </Container>
            </SectionCL>
            <SectionCL
              id=""
              color="light-gray"
              imgPosition="right" //@ts-ignore
              imgUrl={require('static/img-community.jpg')}
            >
              <Container>
                <Row>
                  <Col>
                    <h4 className="section-heading">Join a loving, supportive community.</h4>
                    <IconListCL>
                      <>
                        Share and <b>celebrate</b> your wins.
                      </>
                      <>
                        Find connections with <b>others who GET it</b> and have faced similar situations.
                      </>
                      <>
                        Get support so you <b>never feel alone,</b> no matter what comes up.
                      </>
                    </IconListCL>
                  </Col>
                </Row>
              </Container>
            </SectionCL>
            <SectionCL id="cta-3" color="purple" innerClass="text-center text-white">
              <Container>
                <Row>
                  <Col>
                    <h3 className="section-heading">You get all that and a LOT more in WHOLE Life Club.</h3>
                    <h3 className="mt-0">Join now for just $1 – for your first month</h3>
                    <CheckoutButton sku={productSku} price={Number(productValue).toFixed(2)} slug={productSlug}>
                      <FontAwesomeIcon className="mr-2" icon={faCartPlus} /> Buy Now
                    </CheckoutButton>
                    <SalesDisclaimer iconColor="white" />
                    <p className="balance-text text-small text-center mt-2 mb-0">
                      Join WHOLE Life Club for just $1 for your first month. After that, you’ll be enrolled for a
                      special membership price of just $197 a year (a 60% savings!) — unless you cancel.
                    </p>
                    <h5 className="mt-4 mb-2">This opportunity expires in:</h5>
                    <CountdownTimer time={salesEnd} />
                  </Col>
                </Row>
              </Container>
            </SectionCL>
          </>
        )}
        <CommentSection
          id="comments"
          url={commentsUrl}
          className={classNames(`section-broadcast-comments`, !isSales && `comments-evergreen`)}
          numPosts="10"
          broadcastPage
        />
      </div>
      <Footer />
    </div>
  );
}
